/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import {
    array,
    arrayOf,
    bool, func, object, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Heading from './Partials/Heading';
import MobileUIContainer from './Partials/MobileGridUIContainer';
import DesktopUIContainer from './Partials/GridUIContainer';
import noop from '../../../../../helpers/noop';
import NavigationPillsContainer from '../../../EnterpriseID/Common/NavigationPillsContainer';
import { emitCustomTrackingEvent } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import NavigationPillsContainerV2 from '../../../EnterpriseID/Common/NavigationPillsContainerV2';

const useStyles = makeStyles((theme) => ({
    main: (styles) => ({
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        padding: styles?.padding || '32px 0px',
        [theme.breakpoints.down(600)]: {
            padding: styles?.padding || '16px 0px',
            marginTop: styles?.marginTop ? styles?.marginTop : '48px',
        },
    }),
    root: {
        maxWidth: '1440px',
        margin: '0 auto',
        display: 'flex',
        gap: '16px',
        [theme.breakpoints.down(600)]: {
            padding: '0 16px',
        },
    },
    newDesign: {
        paddingRight: '0',
    },
    recentlyViewedSectionBGColor: (styles) => ({
        backgroundColor: styles?.backgroundColor,
    }),
}));

export default function PersonalizedHomePageGridUIV2({
    products, cmsData, isNewDesign, productStylingConfig, personalizedCarousel,
    handleClickCallback, navPillsUID, attributionToken, dataTestId,
}) {
    const isMobile = useMediaQuery('(max-width: 600px)');
    const dispatch = useDispatch();
    const ffIsTrendingUxUpdateEnabled = useSelector(getFeatureFlag('is-trending-ux-update-enabled'));

    const {
        min_products_to_show,
        padding,
        productTitleWeight,
        backgroundColor = '#fff',
    } = cmsData || {};

    // Recently viewed title and subtitle
    const title = productStylingConfig?.main_title;
    const subTitle = productStylingConfig?.subtitle || '';
    const showSubTitle = productStylingConfig?.show_subtitle;
    const marginTop = productStylingConfig?.margin_top || '';

    const centerAligned = personalizedCarousel?.product_alignment === 'center';

    const classes = useStyles({
        backgroundColor,
        padding,
        marginTop,
    });

    const productTitleStyling = productStylingConfig?.title_styling || {};
    const productSubtitleStyling = productStylingConfig?.subtitle_styling || {};
    const productsCustomStyles = {
        fontFamily: productTitleStyling?.title_font_family,
        fontSize: productTitleStyling?.title_font_size,
        fontStyle: productTitleStyling?.title_font_style,
        fontWeight: productTitleWeight || productStylingConfig?.productTitleWeight,
        color: productTitleStyling?.title_font_color?.color,
        padding: productTitleStyling?.padding,
        titleMargin: productTitleStyling?.title_margin,
        fontFamily1: productSubtitleStyling?.font_family,
        fontSize1: productSubtitleStyling?.font_size,
        fontStyle1: productSubtitleStyling?.font_style,
        color1: productSubtitleStyling?.font_color?.color,
        backgroundColor,
        textAlign: productTitleStyling?.align_title,
    };

    const handleProductClick = (partNumber) => {
        dispatch(emitCustomTrackingEvent({
            eventType: 'custom',
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: 'Trending Product',
            label: partNumber,
        }));
        handleClickCallback(partNumber);
    };

    const navigationPills = ffIsTrendingUxUpdateEnabled
        ? <NavigationPillsContainerV2 maxWidth="1400px" navPillsUID={navPillsUID} />
        : <NavigationPillsContainer maxWidth="1400px" navPillsUID={navPillsUID} />;

    if (isMobile) {
        return (
            <div className={`${classes.main} ${classes.recentlyViewedSectionBGColor}`}>
                {!productStylingConfig?.show_nested_title && (
                    <div className={classes.root}>
                        <Heading
                            title={title}
                            subTitle={subTitle || ''}
                            styles={productsCustomStyles}
                            showSubTitle={showSubTitle}
                        />
                    </div>
                )}
                {navPillsUID && navigationPills}
                <div className={`${classes.root} ${isNewDesign ? classes.newDesign : ''}`}>
                    <MobileUIContainer
                        products={products}
                        minRowsToShow={productStylingConfig?.number_of_rows_to_display || 1}
                        trackClickThrough={(partNumber) => handleProductClick(partNumber)}
                        containerBGColor={backgroundColor}
                        productStyleConfig={productStylingConfig}
                        centerAligned={centerAligned}
                        attributionToken={attributionToken}
                        dataTestId={dataTestId}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={`${classes.main}`}>
            <div className={classes.root}>
                {!productStylingConfig?.show_nested_title && (
                    <Heading
                        title={title}
                        subTitle={subTitle || ''}
                        styles={productsCustomStyles}
                        showSubTitle={showSubTitle}
                    />
                )}
            </div>
            {navPillsUID && navigationPills}
            <div className={classes.root}>
                <DesktopUIContainer
                    products={products}
                    minRowsToShow={productStylingConfig?.number_of_rows_to_display || 1}
                    productClickCallback={(partNumber) => handleProductClick(partNumber)}
                    recentlyViewedSectionBGColor={backgroundColor}
                    productStyleConfig={productStylingConfig}
                    centerAligned={centerAligned}
                    attributionToken={attributionToken}
                    dataTestId={dataTestId}
                />
            </div>
        </div>
    );
}

PersonalizedHomePageGridUIV2.propTypes = {
    products: array,
    cmsData: object,
    isNewDesign: bool,
    productStylingConfig: object,
    personalizedCarousel: object,
    handleClickCallback: func,
    navPillsUID: string,
    attributionToken: string,
    dataTestId: string,
};

PersonalizedHomePageGridUIV2.defaultProps = {
    products: [],
    cmsData: {},
    isNewDesign: false,
    productStylingConfig: {},
    personalizedCarousel: {},
    handleClickCallback: noop,
    navPillsUID: '',
    attributionToken: '',
    dataTestId: '',
};
